import { __, setLocale } from '../translator';
import { updateStatusAfterSubmit } from './auto-save';

class LandingCalendarAPI {
    // Calendar API accepts es|en|pt|ca only.
    static normalizeCalendarApiLanguage(lang) {
        const allowed = new Set(['es', 'en', 'pt', 'ca']);
        const raw = String(lang ?? 'en').trim().toLowerCase();
        if (allowed.has(raw)) {
            return raw;
        }
        const two = raw.slice(0, 2);
        return allowed.has(two) ? two : 'en';
    }

    constructor({ csrfToken, lang, timezone = 'UTC', inscriptionGuid = null, slug = null, embedTheme = null, redirectEnabled = 'true', thankYouText = null, redirectFalseShowThankYouAndReset = true, embedFormLayout = 'standard' } = {}, endpoints = {}) {
        if (!csrfToken || !lang) {
            console.error('LandingCalendarAPI: csrfToken and lang are required');
            return;
        }

        this.csrfToken = csrfToken;
        this.lang = lang;
        this.apiLang = LandingCalendarAPI.normalizeCalendarApiLanguage(lang);
        this.timezone = timezone;
        this.inscriptionGuid = inscriptionGuid;
        this.slug = slug;
        this.embedTheme = embedTheme;
        this.redirectEnabled = redirectEnabled;
        this.thankYouText = thankYouText;
        this.redirectFalseShowThankYouAndReset = redirectFalseShowThankYouAndReset;
        this.embedFormLayout = embedFormLayout;
        this.currentFromDate = new Date().toISOString().split('T')[0];
        this.nextDate = null;
        this.currentCalendarData = null;
        // Stops late calendar API responses from repainting after a booking.
        this.calendarBookingLifecycle = 'idle';
        this._datesLoadCacheKey = null;
        this._datesLoadInFlight = false;
        this._suppressTimezoneReload = false;

        this.$calendarField = $('.calendar-field');
        this.prevDatesStack = JSON.parse(localStorage.getItem('calendarPrevDatesStack') || '[]');

        this.endpoints = Object.assign(
            {
                availableDates: '/landing/api/available-dates',
                availableTimes: '/landing/api/available-times',
                storeAppointment: '/landing/api/store-appointment'
            },
            endpoints
        );

        this.setupEventListeners();

        setLocale(this.apiLang);

        this.bootstrapCalendarIfReady();
    }

    /**
     * If the user picked a modality before autosave created the inscription, calendarShow may have
     * run with no API instance. Load dates once we have both inscription GUID and a visible calendar.
     */
    bootstrapCalendarIfReady() {
        if (!this.inscriptionGuid) {
            return;
        }
        if (this.calendarBookingLifecycle === 'done' || this.calendarBookingLifecycle === 'booking') {
            return;
        }
        const modality = this.getAppointmentType();
        if (!modality || modality === 'whatsapp') {
            return;
        }
        const $wrap = $('#calendar-wrapper');
        if (!$wrap.length || $wrap.hasClass('hidden')) {
            return;
        }
        if (this.currentCalendarData) {
            return;
        }
        this.loadAvailableDates(this.currentFromDate, modality);
    }

    // Helpers
    request(url, data, onSuccess, onError) {
        $.ajax({
            url,
            type: 'POST',
            data: { ...data, _token: this.csrfToken },
            success: (res) => {
                if (url === '/continue-on-whatsapp') {
                    onSuccess(res);
                } else if (res.error === false && res.data) {
                    onSuccess(res.data);
                } else {
                    onError?.(res.messages || __('landing.calendar_request_failed'));
                }
            },
            error: (xhr, __, err) => {
                const body = xhr.responseJSON;
                const validation = body && body.messages;
                console.error(`${url} error (${xhr.status}):`, validation || body || err);
                onError?.(validation || body || __('error_occurred'));
            }
        });
    }

    // Push a GTM dataLayer event when a qualified lead reaches the calendar or WhatsApp step.
    // Marketing uses this event to count campaign conversions, since only qualified leads
    // (is_qualified === true) ever reach these destinations.
    pushQualifiedLeadEvent(method) {
        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'qualified_lead',
                method,
                landing: this.slug,
                inscription_guid: this.inscriptionGuid,
                is_qualified: $(document).data('isQualified') === true,
            });
        } catch (e) {
            console.error('Failed to push qualified_lead dataLayer event:', e);
        }
    }

    getAppointmentType(howContinueOverride = null) {
        const normalize = (raw) => {
            const v = raw !== null && raw !== undefined ? String(raw).trim() : '';
            if (v !== '' && v !== 'whatsapp') {
                return v;
            }
            return '';
        };

        if (howContinueOverride !== null && howContinueOverride !== undefined) {
            return normalize(howContinueOverride);
        }

        const $how = $('#how_continue');
        if ($how.length) {
            return normalize($how.val());
        }
        const $other = $('[data-calendar="true"]').not('#how_continue').first();
        if ($other.length) {
            return normalize($other.val());
        }
        return '';
    }

    correctTimezone(timezone) {
        const timezoneMapping = {
            'America/Buenos_Aires': 'America/Argentina/Buenos_Aires'
        };

        return timezoneMapping[timezone] || timezone;
    }

    getUserTimezone() {
        const timezone = localStorage.getItem('selected_timezone') || this.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        return this.correctTimezone(timezone);
    }

    buildDatesLoadCacheKey(fromDate, appointmentType) {
        return `${appointmentType}::${fromDate}`;
    }

    shouldSkipDatesReload(fromDate, appointmentType, force = false) {
        if (force) {
            return false;
        }
        if (this._datesLoadInFlight) {
            return true;
        }
        const key = this.buildDatesLoadCacheKey(fromDate, appointmentType);
        return key === this._datesLoadCacheKey && !!this.currentCalendarData;
    }

    invalidateDatesLoadCache() {
        this._datesLoadCacheKey = null;
        this.currentCalendarData = null;
    }

    updateInscriptionGuid(guid, { reload = false } = {}) {
        this.inscriptionGuid = guid;

        // Optionally reload available dates
        if (reload && typeof this.loadAvailableDates === 'function') {
            this.loadAvailableDates();
        } else {
            this.bootstrapCalendarIfReady();
        }
        return this;
    }

    buildUrlParams() {
        const params = new URLSearchParams();
        if (this.slug && this.inscriptionGuid) {
            const tokenData = this.slug + '|' + this.inscriptionGuid;
            const token = btoa(tokenData);
            params.append('token', token);
        }
        if (this.embedTheme) {
            params.append('embed_theme', this.embedTheme);
        }
        if (this.apiLang) {
            params.append('language', this.apiLang);
        }
        return params.toString();
    }

    // Reusable method for AJAX redirect with form submission header
    // When redirect=false, include in postMessage
    redirectWithFormHeader(url, onSuccess = null, onError = null, finalData = null) {
        if (
            typeof window.landingEmbedWizardSubmitStart === 'function'
            && typeof window.landingEmbedWizardThankYou === 'function'
            && window.landingEmbedWizardThankYou(this.embedFormLayout)
        ) {
            window.landingEmbedWizardSubmitStart(this.embedFormLayout);
        }
        $('#btn-register').prop('disabled', true).addClass('is-loading');
        const redirectDisabledRaw = this.redirectEnabled === 'false' || this.redirectEnabled === false || String(this.redirectEnabled).toLowerCase() === 'false';
        const redirectDisabled = redirectDisabledRaw;
        const usePostMessage = !!(window.parent && window.parent !== window);
        const requestPayload = { url, method: 'GET', headers: { 'X-Form-Submitted': 'true', 'X-Requested-With': 'XMLHttpRequest' } };
        console.log('[Embed calendar] Request payload:', requestPayload);
        $.ajax({
            url: url,
            type: 'GET',
            headers: {
                'X-Form-Submitted': 'true',
                'X-Requested-With': 'XMLHttpRequest'
            },
            success: () => {
                if (usePostMessage && parent?.postMessage) {
                    let fullRedirectUrl = window.location.origin + (url.indexOf('/') === 0 ? url : '/' + url);
                    const postMessagePayload = { type: 'form_submission_success', redirect_url: fullRedirectUrl };
                    if (finalData && typeof finalData === 'object') {
                        postMessagePayload.final_data = finalData;
                        try {
                            const encoded = encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(finalData)))));
                            fullRedirectUrl += (fullRedirectUrl.indexOf('?') !== -1 ? '&' : '?') + 'embed_final=' + encoded;
                            postMessagePayload.redirect_url = fullRedirectUrl;
                        } catch (e) {
                            console.warn('[Embed calendar] Could not encode final_data for URL:', e);
                        }
                    }
                    console.log('[Embed calendar] PostMessage payload:', postMessagePayload);
                    parent.postMessage(postMessagePayload, '*');
                    const treatAsFalse = this.redirectFalseShowThankYouAndReset === false || String(this.redirectFalseShowThankYouAndReset).toLowerCase() === 'false';
                    if (!treatAsFalse) {
                        $('#btn-submit-wrapper').find('.submit-success-msg').remove();
                        const message = typeof window.landingEmbedResolveThankYouMessage === 'function'
                            ? window.landingEmbedResolveThankYouMessage(this.thankYouText)
                            : (this.thankYouText || $('#btn-register').attr('data-thank-you-text') || __('landing.embed_wizard_thank_you_fallback'));
                        const useWizardThankYou = typeof window.landingEmbedWizardThankYou === 'function' && window.landingEmbedWizardThankYou(this.embedFormLayout);
                        if (useWizardThankYou) {
                            window.__landingQuestionsLoaderInstance?.clearWizardSubmitInFlight?.();
                            window.showLandingEmbedWizardThankYou(message);
                        } else {
                            const safeMessage = typeof window.landingEmbedThankYouEscapeHtml === 'function'
                                ? window.landingEmbedThankYouEscapeHtml(message)
                                : message;
                            $('#btn-submit-wrapper').prepend('<p class="submit-success-msg has-text-success mt-2 mb-2">' + safeMessage + '</p>');
                            $('#btn-register').prop('disabled', false).removeClass('is-loading');
                        }
                        $(document).trigger('embedCalendarRedirectDone');
                    } else {
                        if (typeof window.landingEmbedWizardThankYou === 'function' && window.landingEmbedWizardThankYou(this.embedFormLayout)) {
                            const message = typeof window.landingEmbedResolveThankYouMessage === 'function'
                                ? window.landingEmbedResolveThankYouMessage(this.thankYouText)
                                : (this.thankYouText || $('#btn-register').attr('data-thank-you-text') || __('landing.embed_wizard_thank_you_fallback'));
                            window.__landingQuestionsLoaderInstance?.clearWizardSubmitInFlight?.();
                            window.showLandingEmbedWizardThankYou(message);
                        }
                        $('#form-calendar').find('input:not([type="hidden"]), select, textarea').prop('disabled', true);
                    }
                } else {
                    if (window !== window.top && parent?.postMessage) {
                        parent.postMessage('formSend', '*');
                    }
                    if (onSuccess && typeof onSuccess === 'function') {
                        onSuccess();
                    }
                    window.location.href = url;
                }
            },
            error: () => {
                if (onError && typeof onError === 'function') {
                    onError();
                }
                if (!usePostMessage) {
                    window.location.href = url;
                } else {
                    $('#btn-register').prop('disabled', false).removeClass('is-loading');
                    if (
                        typeof window.landingEmbedRestoreWizardInnerAfterSubmitError === 'function'
                    ) {
                        window.landingEmbedRestoreWizardInnerAfterSubmitError();
                    } else if (
                        typeof window.landingEmbedWizardThankYou === 'function'
                        && window.landingEmbedWizardThankYou(this.embedFormLayout)
                    ) {
                        $('#questions-loader').addClass('hidden');
                        $('.landing-wizard-inner').removeClass('hidden');
                    }
                }
            }
        });
    }

    // Event Listeners
    setupEventListeners() {
        $(document).on('change', 'select[name="how_continue"], input[name="how_continue"]', (e) => {
            if (this.calendarBookingLifecycle !== 'done') {
                this.calendarBookingLifecycle = 'idle';
            }
            localStorage.setItem('calendarPrevDatesStack', JSON.stringify([]));
            this.prevDatesStack = [];
            this.currentFromDate = new Date().toISOString().split('T')[0];
            this.currentCalendarData = null;
            this.invalidateDatesLoadCache();

            const howContinueVal = $(e.target).val();

            if (!howContinueVal) {
                // If empty or undefined, do nothing
                $('#btn-submit-wrapper').addClass('hidden');
                $('#calendar-wrapper').addClass('hidden');
                return;
            }

            if (howContinueVal === 'whatsapp') {
                // WhatsApp: hide calendar, show submit
                $('#calendar-wrapper').addClass('hidden');
                if (typeof window.landingEmbedRestoreCalendarWrapperToDefaultSlot === 'function') {
                    window.landingEmbedRestoreCalendarWrapperToDefaultSlot();
                }
                if (this.embedFormLayout !== 'wizard') {
                    $('#btn-submit-wrapper').removeClass('hidden');
                } else {
                    $('#btn-submit-wrapper').addClass('hidden');
                    window.__landingQuestionsLoaderInstance
                        ?.restoreWizardNavNoCalendar?.();
                }
                $('#btn-register').prop('disabled', false);

                // Remove calendar register handler to avoid override
                $('#btn-register').off('click.landingCalendarRegister');
                $('#btn-register').off('click.landingWhatsapp');

                $('#btn-register').on('click.landingWhatsapp', (event) => {
                    event.preventDefault();
                    // Remove other flows' data
                    localStorage.removeItem('registerFinalData');
                    localStorage.removeItem('calendarFinalData');
                    if (
                        typeof window.landingEmbedWizardSubmitStart === 'function'
                        && typeof window.landingEmbedWizardThankYou === 'function'
                        && window.landingEmbedWizardThankYou(this.embedFormLayout)
                    ) {
                        window.landingEmbedWizardSubmitStart(this.embedFormLayout);
                    }
                    $('#btn-register').prop('disabled', true).addClass('is-loading');
                    const payload = {
                        inscription_guid: this.inscriptionGuid,
                        language: this.apiLang
                    };

                    this.request('/continue-on-whatsapp', payload,
                        (data) => {
                            this.updateLeadStatus();
                            data.updatedAt = Date.now();
                            data.text = typeof window.landingEmbedResolveThankYouMessage === 'function'
                                ? window.landingEmbedResolveThankYouMessage(this.thankYouText, 'whatsapp')
                                : ($('#btn-register').attr('data-thank-you-whatsapp') || this.thankYouText);
                            localStorage.setItem('whatsappFinalData', JSON.stringify(data));
                            const urlParams = this.buildUrlParams();
                            let separator = urlParams ? '?' : '';
                            let params = urlParams || '';
                            if (this.redirectEnabled === 'false') {
                                params = params ? params + '&' : '';
                                params += 'redirect_enabled=false';
                                separator = '?';
                            }
                            this.pushQualifiedLeadEvent('whatsapp');
                            const doneUrl = `/${this.lang}/landing/calendar/whatsapp/done${separator}${params}`;
                            this.redirectWithFormHeader(doneUrl, null, null, data);
                        },
                        (msg) => {
                            console.error('Failed to continue on whatsapp:', msg);
                            window.__landingQuestionsLoaderInstance?.clearWizardSubmitInFlight?.();
                            $('#btn-register').prop('disabled', false).removeClass('is-loading');
                            if (typeof window.landingEmbedRestoreWizardInnerAfterSubmitError === 'function') {
                                window.landingEmbedRestoreWizardInnerAfterSubmitError();
                            }
                        }
                    );
                });
                return;
            } else {
                // Remove WhatsApp handler if switching away
                $('#btn-register').off('click.landingWhatsapp');
            }

            // No bookable modality: submit only
            if (!this.getAppointmentType(howContinueVal)) {
                $('#calendar-wrapper').addClass('hidden');
                if (this.embedFormLayout !== 'wizard') {
                    $('#btn-submit-wrapper').removeClass('hidden');
                } else {
                    $('#btn-submit-wrapper').addClass('hidden');
                    window.__landingQuestionsLoaderInstance
                        ?.restoreWizardNavNoCalendar?.();
                }
                $('#btn-register').prop('disabled', false);
                return;
            }

            $('#btn-submit-wrapper').addClass('hidden');
            $('#calendar-wrapper').removeClass('hidden');

            this.loadAvailableDates(this.currentFromDate, howContinueVal);
        });
        $(document).on('calendarShow', (_e, options = {}) => {
            if (this.calendarBookingLifecycle === 'done' || this.calendarBookingLifecycle === 'booking') {
                return;
            }
            const force = !!(options && options.force);
            if (!force) {
                localStorage.setItem('calendarPrevDatesStack', JSON.stringify([]));
                this.prevDatesStack = [];
                this.currentFromDate = new Date().toISOString().split('T')[0];
                this.currentCalendarData = null;
                this.invalidateDatesLoadCache();
            }
            this.loadAvailableDates(this.currentFromDate, null, { force });
        });
        $(document).on('click', '.calendar-tab-btn', function () {
            if ($(this).is('[disabled]')) {
                return;
            }

            const target = $(this).data('target');

            $('.calendar-tab-content').hide();
            $('.calendar-tab-btn').removeClass('active');
            $(`.calendar-tab-content-${target}`).show();

            $(this).addClass('active');
        });
        $(document).on('click', '.calendar-date-btn', (e) => {
            const $btn = $(e.target).closest('.calendar-date-btn');
            const date = $btn.data('date');

            if (!date) {
                return;
            }

            $('.calendar-date-btn').removeClass('active');
            $btn.addClass('active');
            if (!this.getAppointmentType()) {
                return;
            }
            this.loadAvailableTimes(date, null);
        });
        $(document).on('click', '.calendar-time-btn', (e) => {
            const $btn = $(e.currentTarget);
            const time = $btn.data('time');
            const category = $btn.data('category');

            // Parse users from comma-separated string
            const users = ($btn.data('users') || '').toString().split(',').map(id => parseInt(id, 10)).filter(Boolean);

            // Reset all times first
            $('.calendar-time-btn').removeClass('active');
            $('.calendar-confirm-btn').remove();

            // Mark this as active
            $btn.addClass('active');

            // Make the respective tab active and show only its slots
            $('.calendar-tab-content').hide();
            $(`.calendar-tab-content-${category}`).show();
            $('.calendar-tab-btn').removeClass('active');
            $(`.calendar-tab-btn[data-target="${category}"]`).addClass('active');

            // Add confirm button next to it (spinner shown when disabled)
            const $confirm = $(`
                <button type="button" class="calendar-confirm-btn">
                    <span class="calendar-confirm-btn-spinner"></span>
                    <span class="calendar-confirm-btn-text">${__('agenda-event.confirm')}</span>
                </button>
            `);
            $btn.after($confirm);

            // Handle confirm action
            $confirm.on('click', () => {
                this.storeAppointment({ time, users });
            });
        });
        $(document).on('change', 'select[name="timezone"]', (e) => {
            if (this._suppressTimezoneReload) {
                return;
            }

            const selectedTz = $(e.target).val();
            if (!selectedTz) {
                return;
            }

            const previousTz = localStorage.getItem('selected_timezone');
            localStorage.setItem('selected_timezone', selectedTz);
            this.timezone = selectedTz;

            if (previousTz === selectedTz && this.currentCalendarData) {
                return;
            }

            this.invalidateDatesLoadCache();
            $('select[name="timezone"]').not(e.target).val(selectedTz).each(function () {
                const $select = $(this);
                if ($select.data('select2')) {
                    $select.trigger('change.select2');
                }
            });
            this.loadAvailableDates(this.currentFromDate, null, { force: true });
        });
        $(document).on('inscriptionGuidUpdated', (_, guid) => this.inscriptionGuid = guid);
    }

    // API Calls
    loadAvailableDates(fromDate = this.currentFromDate, howContinueOverride = null, { force = false } = {}) {
        if (!this.inscriptionGuid) {
            return console.warn('No inscription GUID');
        }
        if (this.calendarBookingLifecycle === 'done' || this.calendarBookingLifecycle === 'booking') {
            return;
        }

        const appointmentType = this.getAppointmentType(howContinueOverride);
        if (!appointmentType) {
            return;
        }

        if (this.shouldSkipDatesReload(fromDate, appointmentType, force)) {
            return;
        }

        this._datesLoadInFlight = true;

        // Hide all calendar sections before fetching
        this.$calendarField.find('.calendar-duration').addClass('hidden');
        this.$calendarField.find('.calendar-timezone-wrapper').addClass('hidden');
        this.$calendarField.find('.calendar-tabs').addClass('hidden');
        this.$calendarField.find('.calendar-times').addClass('hidden');
        this.$calendarField.find('.title-select-time').addClass('hidden');

        this.showLoader('.calendar-dates-wrapper', __('agenda-event.loading_dates'));
        const payload = {
            appointment_type: appointmentType,
            from_date: fromDate,
            inscription_guid: this.inscriptionGuid,
            timezone: this.getUserTimezone(),
            language: this.apiLang
        };

        this.request(this.endpoints.availableDates, payload,
            (data) => {
                this._datesLoadInFlight = false;
                if (this.calendarBookingLifecycle === 'booking' || this.calendarBookingLifecycle === 'done') {
                    return;
                }
                this.currentCalendarData = data;
                this.currentFromDate = fromDate;
                this.nextDate = data.next_date;
                this._datesLoadCacheKey = this.buildDatesLoadCacheKey(fromDate, appointmentType);
                this.renderCalendarWithDates(data);
            },
            (msg) => {
                this._datesLoadInFlight = false;
                this.showCalendarError(msg);
            }
        );
    }

    loadNextDates() {
        if (!this.nextDate) {
            return console.warn('No next date available');
        }

        const targetFromDate = this.nextDate;
        this.prevDatesStack.push({
            ...this.currentCalendarData,
            _loadedFromDate: this.currentFromDate,
        });
        localStorage.setItem('calendarPrevDatesStack', JSON.stringify(this.prevDatesStack));
        this.loadAvailableDates(targetFromDate, null, { force: true });
    }

    loadAvailableTimes(date, howContinueOverride = null) {
        if (!this.inscriptionGuid) {
            return console.warn('No inscription GUID');
        }

        const appointmentType = this.getAppointmentType(howContinueOverride);
        if (!appointmentType) {
            return;
        }

        // Hide tabs and times before fetching
        this.$calendarField.find('.calendar-tabs').addClass('hidden');
        this.$calendarField.find('.title-select-time').addClass('hidden');

        this.showLoader('.calendar-times', __('agenda-event.loading_available_times'));

        const payload = {
            appointment_type: appointmentType,
            date,
            inscription_guid: this.inscriptionGuid,
            timezone: this.getUserTimezone(),
            language: this.apiLang
        };

        this.request(this.endpoints.availableTimes, payload,
            (data) => {
                if (this.calendarBookingLifecycle === 'booking' || this.calendarBookingLifecycle === 'done') {
                    return;
                }
                this.renderCalendarTimes(data.available_times);
            },
            (msg) => {
                console.error('Failed to load times:', msg)
            }
        );
    }

    storeAppointment({ time, users }) {
        const notyConfig = {
            layout: 'topCenter',
            timeout: 3000,
            animation: {
                open: 'noty_effects_open',
                close: 'noty_effects_close',
            },
            textAlign: 'center'
        };

        if (!this.inscriptionGuid) {
            new Noty({
                ...notyConfig,
                type: 'error',
                text: __('landing.calendar_error_no_inscription_guid')
            }).show();
            return;
        }

        const date = $('.calendar-date-btn.active').data('date');
        if (!date) {
            new Noty({
                ...notyConfig,
                type: 'warning',
                text: __('landing.calendar_warn_select_date_first')
            }).show();
            return;
        }

        this.toggleCalendarUI(false);
        this.calendarBookingLifecycle = 'booking';

        const payload = {
            appointment_type: this.getAppointmentType(),
            date,
            inscription_guid: this.inscriptionGuid,
            timezone: this.getUserTimezone(),
            language: this.apiLang,
            users,
            slot_time: time,
            _token: this.csrfToken,
            is_qualified: $(document).data('isQualified')
        };

        this.request(this.endpoints.storeAppointment, payload,
            (data) => {
                this.calendarBookingLifecycle = 'done';
                new Noty({
                    ...notyConfig,
                    type: 'success',
                    text: __('landing.calendar_success_appointment_stored')
                }).show();

                // Store response in local storage and redirect only if successful
                try {
                    // Remove other flows' data
                    localStorage.removeItem('registerFinalData');
                    localStorage.removeItem('whatsappFinalData');
                    data.updatedAt = Date.now();
                    localStorage.setItem('calendarFinalData', JSON.stringify(data));
                    const urlParams = this.buildUrlParams();
                    let separator = urlParams ? '?' : '';
                    let params = urlParams || '';
                    if (this.redirectEnabled === 'false') {
                        params = params ? params + '&' : '';
                        params += 'redirect_enabled=false';
                        separator = '?';
                    }
                    this.pushQualifiedLeadEvent('calendar');
                    const doneUrl = `/${this.lang}/landing/calendar/${this.getAppointmentType()}/done${separator}${params}`;
                    this.redirectWithFormHeader(doneUrl, null, null, data);
                } catch (e) {
                    console.error('Failed to store calendarFinalData in localStorage:', e);
                }
            },
            (msg) => {
                this.calendarBookingLifecycle = 'idle';
                console.error('Failed to store appointment:', msg);

                let raw = JSON.stringify(msg);
                let parsed = null;

                try {
                    if (msg.server && msg.server[0]) {
                        parsed = JSON.parse(msg.server[0]);
                    }
                } catch (e) {
                    console.warn("Failed to parse error JSON:", e);
                }

                const notFound = /not\s*found/i.test(raw) || (parsed?.error?.errors || []).some(e => e.reason === 'notFound');

                if (notFound) {
                    new Noty({
                        ...notyConfig,
                        type: 'warning',
                        text: __('landing.calendar_warn_slot_unavailable_refresh'),
                        timeout: 4000
                    }).show();

                    this.loadAvailableTimes(date);
                } else {
                    new Noty({
                        ...notyConfig,
                        type: 'error',
                        text: __('landing.calendar_error_book_failed'),
                        timeout: 4000
                    }).show();
                }

                this.toggleCalendarUI(true);
            }
        );
    }

    renderCalendarWithDates({ available_dates = [], timezone, duration, date_range = '', next_date }) {
        if (!this.$calendarField.length) {
            return console.error('Calendar field not found');
        }
        if (this.calendarBookingLifecycle === 'booking' || this.calendarBookingLifecycle === 'done') {
            return;
        }

        // Update tz + duration (do not re-fetch dates — change on timezone would reload the week)
        const tzToSet = this.getUserTimezone();
        const $tzSelect = this.$calendarField.find('select[name="timezone"]');
        if (tzToSet && $tzSelect.length && $tzSelect.val() !== tzToSet) {
            this._suppressTimezoneReload = true;
            try {
                $tzSelect.val(tzToSet);
                if ($tzSelect.data('select2')) {
                    $tzSelect.trigger('change.select2');
                }
            } finally {
                this._suppressTimezoneReload = false;
            }
        }
        if (duration) {
            this.$calendarField.find('#calendar-duration').text(`${__('agenda-event.duration')}: ${duration} ${__('agenda-event.minutes')}`);
        }

        // Reset UI
        this.$calendarField.find('.calendar-duration').addClass('hidden');
        this.$calendarField.find('.calendar-timezone-wrapper').addClass('hidden');
        this.$calendarField.find('.calendar-tabs').addClass('hidden');
        this.$calendarField.find('.calendar-times').addClass('hidden');

        // Disable prev if fromDate is currentFromDate
        const disablePrev = !this.prevDatesStack.length;
        const disableNext = !next_date;

        // Restore calendar-dates-wrapper markup
        this.$calendarField.find('.calendar-dates-wrapper').html(
            this.getCalendarDatesWrapperMarkup(date_range, disablePrev, disableNext)
        );

        // Render dates
        const $dates = this.$calendarField.find('.calendar-dates').empty();
        if (available_dates.length) {
            available_dates.forEach(({ date, day_name, month_name }) => {
                const dayNumber = date.split('-')[2];
                $dates.append(`
                    <button type="button" class="calendar-date-btn" data-date="${date}">
                        <div class="day-name">${day_name.toUpperCase()}</div>
                        <div class="date">${dayNumber}</div>
                        <div class="month-name">${month_name.toUpperCase()}</div>
                    </button>
                `);
            });

            // Show duration and timezone sections
            this.$calendarField.find('.calendar-duration').removeClass('hidden');
            this.$calendarField.find('.calendar-timezone-wrapper').removeClass('hidden');
            this.$calendarField.find('.title-select-time').addClass('hidden');
            
            const $firstDateBtn = this.$calendarField.find('.calendar-date-btn').first();
            if ($firstDateBtn.length) {
                $firstDateBtn.addClass('active');
                const firstDate = $firstDateBtn.data('date');
                $('#selected_date').val(firstDate);
                if (this.getAppointmentType()) {
                    this.loadAvailableTimes(firstDate);
                }
            }
        } else {
            $dates.html(`<p class="text-center">${__('landing.calendar_no_available_dates')}</p>`);
        }

        this.updateNavigationButtons();
    }

    renderCalendarTimes(times) {
        if (this.calendarBookingLifecycle === 'booking' || this.calendarBookingLifecycle === 'done') {
            return;
        }
        const $times = this.$calendarField.find('.calendar-times').empty();
        const $tabsHeader = this.$calendarField.find('.calendar-tabs').empty();

        // Show tabs and times sections when times are fetched
        this.$calendarField.find('.calendar-tabs').removeClass('hidden');
        this.$calendarField.find('.calendar-times').removeClass('hidden');
        this.$calendarField.find('.title-select-time').removeClass('hidden');

        // Group by category
        const groups = times?.reduce((acc, t) => {
            (acc[t.category || 'general'] ||= []).push(t);
            return acc;
        }, {}) || {};

        const categories = [
            { key: 'morning', label: __('agenda-event.morning') },
            { key: 'day', label: __('agenda-event.day') },
            { key: 'night', label: __('agenda-event.night') }
        ];

        categories.forEach(({ key, label }) => {
            const group = groups[key] || [];

            // Tab button
            $tabsHeader.append(`
                <button type="button" class="calendar-tab-btn" data-target="${key}">
                    ${label}
                </button>
            `);

            // Tab content: always hidden initially
            const $tab = $(`<div class="calendar-tab-content calendar-tab-content-${key}" data-category="${key}" style="display:none;"></div>`);
            if (group.length) {
                group.forEach(({ time, users }) => {
                    const usersAttr = (users || []).join(',');
                    $tab.append(`
                        <div class="calendar-time-row">
                            <button type="button" class="calendar-time-btn" data-time="${time}" data-category="${key}" data-users="${usersAttr}">
                                ${time}
                            </button>
                        </div>
                    `);
                });
            } else {
                $tab.append(`<div class="no-slots">${__('landing.no_time_slots')}</div>`);
            }
            $times.append($tab);
        });

        // Show only the first tab with slots
        const firstTabWithSlots = categories.find(category => (groups[category.key] || []).length > 0);
        if (firstTabWithSlots) {
            $times.find(`.calendar-tab-content-${firstTabWithSlots.key}`).show();
            $tabsHeader.find(`.calendar-tab-btn[data-target="${firstTabWithSlots.key}"]`).addClass('active');
        }
    }

    // Navigation + Errors
    updateNavigationButtons() {
        const $prev = this.$calendarField.find('.btn-navigate').first();
        const $next = this.$calendarField.find('.btn-navigate').last();

        // Prev
        if (this.prevDatesStack.length) {
            $prev.removeClass('disabled').prop('disabled', false).off().on('click', (e) => {
                e.preventDefault();
                const prev = this.prevDatesStack.pop();
                localStorage.setItem('calendarPrevDatesStack', JSON.stringify(this.prevDatesStack));
                if (prev) {
                    this.currentCalendarData = prev;
                    this.nextDate = prev.next_date;
                    const restoredFromDate =
                        prev._loadedFromDate
                        || (prev.available_dates && prev.available_dates[0] && prev.available_dates[0].date)
                        || this.currentFromDate;
                    this.currentFromDate = restoredFromDate;
                    const appointmentType = this.getAppointmentType();
                    if (appointmentType) {
                        this._datesLoadCacheKey = this.buildDatesLoadCacheKey(restoredFromDate, appointmentType);
                    }
                    this.renderCalendarWithDates(prev);
                }
            });
        } else {
            $prev.addClass('disabled').prop('disabled', true).off();
        }

        // Next
        if (this.nextDate) {
            $next.removeClass('disabled').prop('disabled', false).off().on('click', (e) => {
                e.preventDefault();
                this.loadNextDates();
            });
        } else {
            $next.addClass('disabled').prop('disabled', true).off();
        }
    }

    showLoader(targetSelector, text = __('agenda-event.loading_generic')) {
        this.$calendarField.find(targetSelector).html(`
            <div class="text-center p-4">
                <p class="mt-2">${text}</p>
                <div class="spinner"></div>
            </div>
        `);
    }

    showCalendarError(msg) {
        const displayMsg = typeof msg === 'string' && msg.trim() !== ''
            ? msg
            : __('landing.calendar_dates_load_failed');
        // Restore wrapper markup before showing error
        this.$calendarField.find('.calendar-dates-wrapper').html(
            this.getCalendarDatesWrapperMarkup('', !this.prevDatesStack.length, !this.nextDate)
        );
        const $container = $('<div class="text-center p-4"></div>');
        $container.append($('<p class="text-danger"></p>').text(displayMsg));
        $container.append(
            $('<button type="button" class="btn btn-primary"></button>')
                .text(__('agenda-event.retry'))
                .on('click', () => {
                    $(document).trigger('calendarShow', [{ force: true }]);
                })
        );
        this.$calendarField.find('.calendar-dates-wrapper').empty().append($container);
    }

    getCalendarDatesWrapperMarkup(dateRange = '', disablePrev = false, disableNext = false) {
        const formattedRange = dateRange.replace(/\./g, '').replace(/\b\w/g, c => c.toUpperCase());
        return `
            <p class="title-select-date">${__('landing.select_date')}</p>
            <div class="d-flex justify-content-between align-items-center mt-2">
                <button type="button" class="btn btn-navigate mr-4 ${disablePrev ? 'disabled' : ''}">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <span class="small fw-medium">${formattedRange}</span>
                <button type="button" class="btn btn-navigate ml-4 ${disableNext ? 'disabled' : ''}">
                    <i class="fas fa-chevron-right"></i>
                </button>
            </div>
            <div class="calendar-dates mt-3"></div>
        `;
    }

    toggleCalendarUI(enabled) {
        const $confirmBtn = $('.calendar-confirm-btn');
        $confirmBtn.prop('disabled', !enabled).toggleClass('is-loading', !enabled);
        $confirmBtn.find('.calendar-confirm-btn-text').text(enabled ? __('agenda-event.confirm') : __('agenda-event.submitting'));

        $('.calendar-date-btn').prop('disabled', !enabled).toggleClass('disabled', !enabled);
        $('.calendar-time-btn').prop('disabled', !enabled).toggleClass('disabled', !enabled);
        $('.calendar-tab-btn').prop('disabled', !enabled).toggleClass('disabled', !enabled);
    }

    updateLeadStatus() {
        updateStatusAfterSubmit({
            _token: this.csrfToken,
            inscription_guid: this.inscriptionGuid,
            is_qualified: $(document).data('isQualified')
        });
    }
}

// Make it available globally
window.LandingCalendarAPI = LandingCalendarAPI;
